import { supabase } from '../services/supabase/client';

/**
 * Migrate properties and inspections from localStorage to Supabase
 * This is a one-time migration utility
 */
export async function migrateLocalStorageToSupabase() {
  try {
    console.log('🔄 Starting migration from localStorage to Supabase...');

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const userId = user.id;
    const propertiesKey = `properties_${userId}`;
    const inspectionsKey = `inspections_${userId}`;

    // Get data from localStorage
    const localProperties = JSON.parse(localStorage.getItem(propertiesKey) || '[]');
    const localInspections = JSON.parse(localStorage.getItem(inspectionsKey) || '[]');

    console.log(`📦 Found ${localProperties.length} properties and ${localInspections.length} inspections in localStorage`);

    if (localProperties.length === 0 && localInspections.length === 0) {
      console.log('✅ No data to migrate');
      return { success: true, message: 'No data to migrate' };
    }

    // Check if data already exists in Supabase
    const { data: existingProperties } = await supabase
      .from('properties')
      .select('id')
      .limit(1);

    if (existingProperties && existingProperties.length > 0) {
      console.log('⚠️  Data already exists in Supabase. Skipping migration.');
      return { success: false, message: 'Data already exists in Supabase' };
    }

    // Map old property IDs to new UUIDs
    const propertyIdMap = {};

    // Migrate properties
    for (const property of localProperties) {
      const { data: newProperty, error } = await supabase
        .from('properties')
        .insert({
          user_id: userId,
          address: property.address,
          type: property.type,
          tenant_name: property.tenantName || null,
          tenant_email: property.tenantEmail || null,
          move_in_date: property.moveInDate || null,
        })
        .select()
        .single();

      if (error) {
        console.error('Error migrating property:', error);
        continue;
      }

      propertyIdMap[property.id] = newProperty.id;
      console.log(`✅ Migrated property: ${property.address}`);
    }

    // Migrate inspections
    for (const inspection of localInspections) {
      const newPropertyId = propertyIdMap[inspection.propertyId];
      if (!newPropertyId) {
        console.warn(`⚠️  Property not found for inspection, skipping`);
        continue;
      }

      const { data: newInspection, error: inspectionError } = await supabase
        .from('inspections')
        .insert({
          property_id: newPropertyId,
          type: inspection.type,
          date: inspection.date,
          inspector: inspection.inspector || null,
          overall_notes: inspection.overallNotes || null,
        })
        .select()
        .single();

      if (inspectionError) {
        console.error('Error migrating inspection:', inspectionError);
        continue;
      }

      // Migrate rooms and items
      if (inspection.rooms) {
        for (const room of inspection.rooms) {
          const { data: newRoom, error: roomError } = await supabase
            .from('inspection_rooms')
            .insert({
              inspection_id: newInspection.id,
              name: room.name,
              display_order: room.displayOrder || 0,
            })
            .select()
            .single();

          if (roomError) {
            console.error('Error migrating room:', roomError);
            continue;
          }

          // Migrate items
          if (room.items) {
            const items = room.items.map((item, index) => ({
              room_id: newRoom.id,
              name: item.name,
              condition: item.condition,
              notes: item.notes || null,
              display_order: index,
            }));

            const { error: itemsError } = await supabase
              .from('inspection_items')
              .insert(items);

            if (itemsError) {
              console.error('Error migrating items:', itemsError);
            }
          }
        }
      }

      console.log(`✅ Migrated inspection for property ${newPropertyId}`);
    }

    console.log('✅ Migration completed successfully!');
    return {
      success: true,
      message: `Migrated ${localProperties.length} properties and ${localInspections.length} inspections`,
    };
  } catch (error) {
    console.error('❌ Migration failed:', error);
    return { success: false, message: error.message };
  }
}

/**
 * Clear localStorage data after successful migration
 */
export function clearLocalStorageData(userId) {
  const propertiesKey = `properties_${userId}`;
  const inspectionsKey = `inspections_${userId}`;

  localStorage.removeItem(propertiesKey);
  localStorage.removeItem(inspectionsKey);

  console.log('🗑️  Cleared localStorage data');
}
